import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { RangeSetBuilder } from '@codemirror/state';
import { Decoration, EditorView, ViewPlugin } from '@codemirror/view';
import { tags } from '@lezer/highlight';

import type { DecorationSet, ViewUpdate } from '@codemirror/view';

const variableMark = Decoration.mark({ class: 'cm-template-variable' });
const ifBlockMark = Decoration.mark({ class: 'cm-template-if' });
const operatorMark = Decoration.mark({ class: 'cm-template-operator' });
const quotedMark = Decoration.mark({ class: 'cm-template-quoted' });

function buildDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  const doc = view.state.doc.toString();

  const regex = /\{\{(.*?)\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(doc)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const inner = match[1].trim();

    if (inner.startsWith('#if') || inner === '/if') {
      builder.add(start, end, ifBlockMark);
    } else {
      builder.add(start, end, variableMark);
    }

    const innerStart = start + match[0].indexOf(inner);
    for (const op of ['||', '==', '!=', '?', ':']) {
      let opIdx = inner.indexOf(op);
      while (opIdx !== -1) {
        builder.add(innerStart + opIdx, innerStart + opIdx + op.length, operatorMark);
        opIdx = inner.indexOf(op, opIdx + op.length);
      }
    }

    const quoteRegex = /(["'])(?:(?=(\\?))\2.)*?\1/g;
    let qMatch: RegExpExecArray | null;
    while ((qMatch = quoteRegex.exec(inner)) !== null) {
      builder.add(
        innerStart + qMatch.index,
        innerStart + qMatch.index + qMatch[0].length,
        quotedMark,
      );
    }
  }

  return builder.finish();
}

const templateHighlightPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view);
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildDecorations(update.view);
      }
    }
  },
  { decorations: (v) => v.decorations },
);

const templateTheme = EditorView.baseTheme({
  '.cm-template-variable': { color: '#2563eb', fontWeight: '500' },
  '.cm-template-if': { color: '#7c3aed', fontWeight: '600' },
  '.cm-template-operator': { color: '#ea580c', fontWeight: '600' },
  '.cm-template-quoted': { color: '#16a34a' },
});

const fallbackHighlight = HighlightStyle.define([
  { tag: tags.angleBracket, color: '#6b7280' },
  { tag: tags.tagName, color: '#dc2626' },
  { tag: tags.attributeName, color: '#2563eb' },
  { tag: tags.attributeValue, color: '#16a34a' },
]);

export const templateLanguageExtension = () => [
  templateHighlightPlugin,
  templateTheme,
  syntaxHighlighting(fallbackHighlight),
];
